import { useCallback, useState } from 'react';
import { ImageIcon, Sparkles, FolderOpen } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { VaultFilePickerPanel, type VaultPickerResult } from '@/components/VaultFilePickerDialog';
import { toast } from '@/components/ui/use-toast';
import { ImageGenerateTab } from './ImageGenerateTab';
import { GeneratedImageResult } from '../hooks';

/**
 * The MediaPickerModal shape preserved for `PostComposer` consumers:
 * each picked / uploaded file boils down to one of these. The Vault
 * picker doesn't materialise this shape; we compute it from the
 * `VaultPickerResult` it returns.
 */
export interface MediaPickerFile {
  fileId: string;
  fileName: string;
  mimeType: string | null;
  sizeBytes: number;
  /** Vault s3Key — re-signable later for durable references. */
  s3Key: string;
  /** Fresh 1-hour download URL for inline preview. */
  downloadUrl: string;
}

export interface MediaPickerModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called with the picked / uploaded files (one callback for both
   * paths after the migration — the picker handles both). */
  onFilesSelected: (files: MediaPickerFile[]) => void;
  /** Called when the user confirms an AI-generated image. */
  onImageGenerated: (result: GeneratedImageResult) => void;
  /** Accepted MIME types string for the Generate tab; the picker
   * tab filters to images + videos. */
  acceptedMimeTypes: string;
  /** Max number of files that can still be added. */
  remainingSlots: number;
}

function pickerResultToMediaFile(r: VaultPickerResult): MediaPickerFile {
  return {
    fileId: r.documentId,
    fileName: r.fileName,
    mimeType: r.mimeType,
    sizeBytes: r.sizeBytes,
    s3Key: r.s3Key,
    downloadUrl: r.downloadUrl,
  };
}

function isAcceptedMimeType(mimeType: string | null, acceptedMimeTypes: string): boolean {
  if (!mimeType) return false;
  const accepted = acceptedMimeTypes.split(',').map((t) => t.trim());
  return accepted.some((acceptedType) => {
    if (acceptedType.endsWith('/*')) {
      return mimeType.startsWith(acceptedType.slice(0, -2));
    }
    return mimeType === acceptedType;
  });
}

export function MediaPickerModal({
  open,
  onOpenChange,
  onFilesSelected,
  onImageGenerated,
  acceptedMimeTypes,
  remainingSlots,
}: MediaPickerModalProps) {
  const [activeTab, setActiveTab] = useState<'browse' | 'generate'>('browse');

  const handlePicked = useCallback(
    (results: VaultPickerResult[]) => {
      // Filter to ones that match the platform's accepted MIME list.
      const accepted = results.filter((r) => isAcceptedMimeType(r.mimeType, acceptedMimeTypes));
      const rejected = results.length - accepted.length;
      if (rejected > 0) {
        toast({
          title: `Skipped ${rejected} file${rejected === 1 ? '' : 's'}`,
          description: 'Some files are not supported by the selected platforms.',
        });
      }
      if (accepted.length === 0) return;
      onFilesSelected(accepted.map(pickerResultToMediaFile));
      onOpenChange(false);
    },
    [acceptedMimeTypes, onFilesSelected, onOpenChange],
  );

  const handleImageConfirmed = useCallback(
    (result: GeneratedImageResult) => {
      onImageGenerated(result);
      toast({ description: 'AI-generated image attached.' });
      onOpenChange(false);
    },
    [onImageGenerated, onOpenChange],
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ImageIcon className="h-5 w-5" />
            Add Media
          </DialogTitle>
          <DialogDescription>
            Browse / upload media from your files, or generate an image with AI. You can add{' '}
            {remainingSlots} more file{remainingSlots === 1 ? '' : 's'}.
          </DialogDescription>
        </DialogHeader>

        <Tabs
          value={activeTab}
          onValueChange={(v) => setActiveTab(v as 'browse' | 'generate')}
          className="flex-1 flex flex-col min-h-0"
        >
          <TabsList className="w-full bg-background rounded-2xl shadow-[var(--shadow-soft-blue)] p-1 gap-2">
            <TabsTrigger
              value="browse"
              className="font-normal text-[12px] flex items-center gap-1.5 flex-1 font-mono rounded-2xl data-[state=active]:bg-primary data-[state=active]:text-primary-foreground hover:bg-muted"
            >
              <FolderOpen className="h-auto w-4" />
              Browse / Upload
            </TabsTrigger>
            <TabsTrigger
              value="generate"
              className="font-normal text-[12px] flex items-center gap-1.5 flex-1 font-mono rounded-2xl data-[state=active]:bg-primary data-[state=active]:text-primary-foreground hover:bg-muted"
            >
              <Sparkles className="h-auto w-4" />
              Generate
            </TabsTrigger>
          </TabsList>

          <TabsContent value="browse" className="flex-1 flex flex-col min-h-0 mt-3">
            <VaultFilePickerPanel
              isActive={open && activeTab === 'browse'}
              multiSelect
              onFilesSelected={handlePicked}
              mimeFilter={(doc) =>
                doc.mimeType ? isAcceptedMimeType(doc.mimeType, acceptedMimeTypes) : false
              }
              uploadAccept={acceptedMimeTypes}
              selectionLimit={remainingSlots}
              confirmLabel={remainingSlots > 0 ? `Attach (max ${remainingSlots})` : 'Attach'}
            />
          </TabsContent>

          <TabsContent value="generate" className="flex-1 min-h-0 mt-3">
            <ImageGenerateTab onImageConfirmed={handleImageConfirmed} />
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
